import { HttpErrorResponse, HttpHandler, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { of, throwError } from 'rxjs';
import { catchError, finalize, tap, timeout } from 'rxjs/operators';
import { CommonService } from 'src/app/services/common.service';
import { api } from 'src/app/crypto-constants/api';
import { isSignupLandingRoute } from 'src/app/modules/authentication/partner-signup/partner-signup-flow';
import { resolvePostLogoutLoginPath } from 'src/app/modules/authentication/auth-login.routes';

@Injectable({
  providedIn: 'root'
})
export class AuthInterceptorService {
  public token : any =  localStorage.getItem('token');
  constructor(
    private _router : Router,
    private commonService: CommonService
    ) { }

  /**
   * B2B: client id for legacy APIs — prefer URL /business/client/:id, then persisted client_details.
   */
  private resolveBusinessClientId(): string | null {
    return this.commonService.resolveB2bClientId();
  }

  private requestHasClientId(req: HttpRequest<any>): boolean {
    if (req.params.has('clientId')) {
      return true;
    }
    return /[?&]clientId=/.test(req.url || '');
  }

  /** Public B2B associate self-registration — must not send session token. */
  private isPublicAssociateRegisterRequest(url: string, req: HttpRequest<any>): boolean {
    if (req.headers.get('skipAuth') === 'true') {
      return true;
    }
    const u = (url || '').toLowerCase();
    return (
      u.includes('/business/companies') || u.includes('/business/registerassociate')
    );
  }

  /** Public advisor/business signup — must use CRYPTO (same as dev /signup flow). */
  private isPublicBusinessRegisterRequest(url: string): boolean {
    return (url || '').toLowerCase().includes('/business/registeruser');
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): any{
    const url = request.url || '';
    const isAssetRequest = url.startsWith('/assets/') || url.includes('/assets/');
    const method = (request.method || 'GET').toUpperCase();
    // Background/page-load reads should not block the UI; only mutations show the global overlay.
    const isBackgroundRead = method === 'GET' || method === 'HEAD' || method === 'OPTIONS';
    const shouldSkipGlobalLoader =
      request.headers.get('skipLoader') === 'true' || isAssetRequest || isBackgroundRead;
    const isPublicAssociateApi = this.isPublicAssociateRegisterRequest(url, request);
    const isPublicBusinessRegister = this.isPublicBusinessRegisterRequest(url);

    if(!shouldSkipGlobalLoader) {
      this.commonService.setLoading(true);
    }
    this.token = localStorage.getItem('token');
    const accountType = localStorage.getItem('accountType') === 'B' ? 'BUSINESS' : 'CRYPTO';

    /**
     * Business client context:
     * When a BUSINESS user is viewing /business/client/:id/*, the legacy module-files API
     * must include ?clientId=:id&accessType=BUSINESS
     */
    try {
      const isBusiness = localStorage.getItem('accountType') === 'B';
      const isModuleFilesApi =
        url.includes('/legacy/list-module-files') ||
        url === api.getModuleData;

      const isLegacyFileListApi =
        url.includes('/legacy/file/list') ||
        url === api.fileVaultGetFiles;

      const isAssetPortfolioApi =
        url.includes('/legacy/assetportfolio/') || // in case of lowercase path somewhere
        url.includes('/legacy/assetPortfolio/') ||   // standard casing used in codebase
        url.includes('/legacy/emergency-vault/assets'); // emergency vault assets API

      const isOnlineAccountsApi =
        url.includes('/legacy/onlineaccount') || // lowercase variant
        url.includes('/legacy/onlineAccount') ||
        url.includes('/legacy/onlineAccountsources') ||
        url.includes('/legacy/directive');

      const isEmergencyBinderApi =
        url.includes('/user/emergencyBinder') ||
        url.includes('/legacy/getEmergencyBinder') ||
        url.includes('/legacy/setEmergencybinderQuorum') ||
        url.includes('/legacy/openEmergencybinder') ||
        url.includes('/legacy/closeEmergencybinder') ||
        url.includes('/legacy/getEmergencyBinderAuditTrail') ||
        url.includes('/legacy/getEmergencyBinderOpenRequests') ||
        url.includes('/legacy/approveEmergencyBinderOpenRequest') ||
        url.includes('/legacy/contacts');

      const isAdditionalBusinessApi =
        url.includes('/legacy/get-user-subscription') ||
        url.includes('/legacy/getUserSubscription') ||
        url.includes('/legacy/file/category') ||
        (url.includes('/caretaker') && !url.includes('/caretaker/ecUpdate')) || // Exclude specific caretaker endpoints if needed
        url.includes('/legacy/list-module-files') ||
        url.includes('/dlsv/creditLimit') ||
        url.includes('/legacy/stripe/creditLimit');

      const isTimeCapsuleApi =
        url.includes('/timeCapsule/dashboard') ||
        url.includes('/timeCapsule/event') ||
        url.includes('/timeCapsule/event_users') ||
        url.includes('/timeCapsule/event_docs');

      if (isBusiness && (isModuleFilesApi || isLegacyFileListApi || isAssetPortfolioApi || isOnlineAccountsApi || isEmergencyBinderApi || isAdditionalBusinessApi || isTimeCapsuleApi)) {
        const clientIdResolved = this.resolveBusinessClientId();

        if (clientIdResolved && clientIdResolved !== 'dashboard') {
          let params = request.params;
          if (!this.requestHasClientId(request)) {
            params = params.set('clientId', clientIdResolved);
          }
          if (!params.has('accessType')) {
            params = params.set('accessType', 'BUSINESS');
          }
          // Stripe credit limit (B2B) expects type=BUSINESS alongside clientId
          if (url.includes('/legacy/stripe/creditLimit') && !params.has('type')) {
            params = params.set('type', 'BUSINESS');
          }
          request = request.clone({ params });
        }
      }

      // B2B: /cryptowill, /user/trust, /user/will require ?type=business (+ clientId in client workspace)
      if (this.commonService.isB2bWillTrustOrCryptowillUrl(url)) {
        request = request.clone({
          params: this.commonService.b2bWillTrustQueryParams(request.params),
        });
      }
    } catch (e) {
      // Never block requests due to interceptor param logic
      // eslint-disable-next-line no-console
      console.warn('[AuthInterceptor] Failed to append BUSINESS clientId/accessType params', e);
    }

    if (isPublicAssociateApi) {
      request = request.clone({
        headers: request.headers
          .delete('skipAuth')
          .delete('skipLoader')
          .set('Content-Type', 'application/json')
          .set('api-req-from', 'BUSINESS'),
      });
    } else if (isPublicBusinessRegister) {
      request = request.clone({
        setHeaders: {
          'x-access-token': '',
          'api-req-from': 'CRYPTO',
        },
      });
    } else {
      request = request.clone({
        setHeaders: {
          'x-access-token': this.token ? this.token : '',
          'api-req-from': accountType,
        },
      });
    }
    return next.handle(request)
    .pipe(
       timeout(1200000),
      tap(evt => {
          if (evt instanceof HttpResponse) {
              if (isPublicAssociateApi || isPublicBusinessRegister || shouldSkipGlobalLoader) {
                return;
              }
              if(evt.body && evt.body.hasOwnProperty('success')){
                if(evt.status == 401 ){
                  // localStorage.clear();
                  // sessionStorage.clear();
                  // this._router.navigate(['/login']);
                  // setTimeout(() => {
                  // window.location.reload();
                  // }, 100);
                }
              }
              else{
                evt.body['success'] =  false;
                evt.body['data'] =  null;
                evt.body['error'] =  "something went wrong!";
              }
          }
      }),
      finalize(() => {
        if(!shouldSkipGlobalLoader) {
          this.commonService.setLoading(false);
        }
      }),
      catchError((err: any) => {
          if(err instanceof HttpErrorResponse) {
              try {
                if(err.status == 401){
          const returnUrl =
            (typeof window !== 'undefined' ? window.location.href : '') || '';
          const stripeSignupReturn =
            returnUrl.includes('session_id=') &&
            (returnUrl.includes('/profile/subscription') ||
              returnUrl.includes('/subscription-management'));
          if (stripeSignupReturn) {
            return throwError(err);
          }
          const isB2b = localStorage.getItem('accountType') === 'B';
                  const path = (window?.location?.pathname || '').toLowerCase();
                  const inBusinessClientWorkspace = path.includes('/business/client/');
                  const isPublicAssociateRegister =
                    path.includes('associate-register') ||
                    (url || '').toLowerCase().includes('/business/companies') ||
                    (url || '').toLowerCase().includes('/business/registerassociate');
                  const isPublicBusinessRegisterApi = this.isPublicBusinessRegisterRequest(url);
                  const onSignupLanding = isSignupLandingRoute();
                  const msg = err?.error?.message || 'Unauthorized';
                  // Public registration: no redirect / token clear (unauthenticated APIs).
                  if (isPublicAssociateRegister || isPublicBusinessRegisterApi) {
                    return throwError(err);
                  }
                  // Referral signup landing — keep advisor/personal token (preview plan links).
                  if (onSignupLanding) {
                    return throwError(err);
                  }
                  // B2B client workspace: 401s are often missing client context on legacy APIs, not a dead session.
                  // Avoid clearing the advisor token so Binder / Files / Online Accounts do not "log out" the user.
                  if (isB2b && inBusinessClientWorkspace) {
                    void this.commonService.showErrorAlert(msg);
                    return throwError(err);
                  }
                  const isDelegationLokblokApi = (url || '')
                    .toLowerCase()
                    .includes('/delegation-admin/lokblok/');
                  const inDelegationWorkspace = path.includes('/delegation');
                  if (isDelegationLokblokApi && inDelegationWorkspace) {
                    return throwError(err);
                  }
                  localStorage.removeItem('token');
                  if (this.commonService.consumeSessionExpiredAlert()) {
                    void this.commonService.showErrorAlert(msg);
                  }
                  this._router.navigate([resolvePostLogoutLoginPath()]);
                }
                else if(err.error && err.error.message) {
                  return throwError(err)
                }
                else{
                  return throwError(err);
                }
              } catch(e) {
                return throwError(e);
              }
          }
          return of(err);
      }));
  }
}


